import moment from "moment";
import C from "../Constants";

// 同種類のイベントか判定
const isSameEventType = (event1, event2) => {
  if (event1.event_type !== event2.event_type) {
    return false;
  }

  return isSameVesselEvent(event1, event2);
};

/**
 *
 * @param {*} event1
 * @param {*} event2
 * @returns
 */
const isSameVesselEvent = (event1, event2) => {
  return (
    isSameVessel(event1, event2) &&
    event1.enterprise_manning_vessel_crew_id ===
      event2.enterprise_manning_vessel_crew_id
  );
};

/**
 * 同じ船舶のレコードかを確認するヘルパー関数
 * @param {*} event1
 * @param {*} event2
 * @returns
 */
const isSameVessel = (event1, event2) => {
  return (
    getRootResource(event1.resource).id === getRootResource(event2.resource).id
  );
};

/**
 * リソースのルート(船舶)を取得する
 * @param {Object} resource - リソース
 */
const getRootResource = resource => {
  if (!resource || !resource.isLeaf) {
    return resource;
  }
  return getRootResource(resource.parent);
};

/**
 * 未登録の乗組員アラートエリアの警戒、注意エリアの塗りつぶし用TimeSpan作成
 * @param {Object} project - Scheduler Pro プロジェクトインスタンス
 * @param {Object} alertSetting - 船員アラート設定マスターから取得した入替船員未登録アラート設定
 * @param {string} alertSetting.id - アラート設定のID
 * @param {Number} alertSetting.caution_threshold_days - 注意アラートの閾値（日数）
 * @param {Number} alertSetting.warning_threshold_days - 警戒アラートの閾値（日数）
 * @param {String} highlightCautinClass - ハイライト用のCSSクラス名（注意）
 * @param {String} highlightWarningClass - ハイライト用のCSSクラス名（警戒）
 * @param {Date} nowDate - 現在の日付
 * @return {Array} 警戒、注意エリアのTimeSpan配列
 */
const createUnregisterdCrewAlertTimeSpan = (
  project,
  alertSetting,
  highlightCautinClass,
  highlightWarningClass,
  nowDate
) => {
  // 警戒、注意エリアのTimeSpan配列
  const highlightTimeSpans = [];

  if (!alertSetting) {
    return highlightTimeSpans;
  }

  // 警戒、注意期間を計算
  // 警戒期間
  const warnStartDate = moment(nowDate);
  const warnEndDate = warnStartDate
    .clone()
    .add(alertSetting.warning_threshold_days, "day");
  // 注意期間
  const cautionStartDate = warnEndDate;
  const cautionEndDate = moment(nowDate).add(
    alertSetting.caution_threshold_days,
    "day"
  );

  // *** 対象リソース抽出 ***
  // 次のリソースを抽出にする
  // 必須船員
  // 展開されているツリーの最下層
  const resourceStore = project.resourceStore;
  const targetResources = resourceStore.query(record => {
    return record.required && !record.isParent && record.ancestorsExpanded();
  });

  for (const targetResouce of targetResources) {
    // *** 対象イベント抽出、ソート ***
    // 次の条件のイベントを抽出
    // 予定ステータス
    // イベントの終了日が現在時刻より後
    // イベントの開始日が警戒終了日より前
    // イベント開始日で昇順ソート
    const events = targetResouce.events.filter(event => {
      return (
        // 予定ステータスがスケジュール
        event.scheduling_status === C.SCHEDULING_STATUS.SCHEDULED &&
        // イベントの終了日が現在時刻より後
        moment(event.endDate).isAfter(nowDate) &&
        // イベントの開始日が警戒終了日より前
        moment(event.startDate).isBefore(cautionEndDate)
      );
    });

    // *** イベントが登録されていない場合 ***
    // 警戒、注意エリアを全て塗りつぶしてコンティニュー
    if (events.length === 0) {
      highlightTimeSpans.push(
        _createHighlightTimeSpan(
          warnStartDate,
          warnEndDate,
          highlightWarningClass,
          targetResouce
        )
      );

      highlightTimeSpans.push(
        _createHighlightTimeSpan(
          cautionStartDate,
          cautionEndDate,
          highlightCautinClass,
          targetResouce
        )
      );
      continue;
    }

    // *** 警戒、注意エリアの塗りつぶし用TimeSpan作成 ***
    // 警戒、注意期間内のイベント間の空白期間を抽出
    // 空白期間配列
    // { from: 開始日時(momentJS), to: 終了日時(momentJS) }
    const blankPeriods = _calculateEventsBlankPeriods(
      events,
      warnStartDate,
      cautionEndDate
    );

    // 空白期間を警戒、注意エリアに分けて塗りつぶす為のTimeSpan配列を作成
    const timeSpans = _createCautionWarnAreaTimeSpans(
      blankPeriods,
      targetResouce,
      warnStartDate,
      warnEndDate,
      cautionStartDate,
      cautionEndDate,
      highlightCautinClass,
      highlightWarningClass
    );

    highlightTimeSpans.push(...timeSpans);
  }

  return highlightTimeSpans;
};

/**
 * 警戒、注意エリアのアラートを塗る為のtimeSpanを作成する関数
 * @param {Array} blankPeriods 空白期間配列 [{ from: 開始日時(momentJS), to: 終了日時(momentJS) }]
 * @param {Object} targetResouce timeSpanに紐づくリソースオブジェクト
 * @param {Date} warnStartDate 警戒開始日時(Date)
 * @param {Date} warnEndDate 警戒終了日時(Date)
 * @param {Date} cautionStartDate 注意開始日時(Date)
 * @param {Date} cautionEndDate 注意終了日時(Date)
 * @param {String} highlightCautinClass - ハイライト用のCSSクラス名（注意）
 * @param {String} highlightWarningClass - ハイライト用のCSSクラス名（警戒）
 * @returns {Array} timeSpan配列
 */
const _createCautionWarnAreaTimeSpans = (
  blankPeriods,
  targetResouce,
  warnStartDate,
  warnEndDate,
  cautionStartDate,
  cautionEndDate,
  highlightCautinClass,
  highlightWarningClass
) => {
  const highlightTimeSpans = [];

  // 空白期間を警戒、注意エリアに分けて塗りつぶす
  for (const blank of blankPeriods) {
    // 開始日時が警戒終了日よりも前の場合は、警戒エリアを塗りつぶす
    if (blank.from.isSameOrBefore(warnEndDate)) {
      highlightTimeSpans.push(
        _createHighlightTimeSpan(
          blank.from.isAfter(warnStartDate) ? blank.from : warnStartDate,
          warnEndDate.isAfter(blank.to) ? blank.to : warnEndDate,
          highlightWarningClass,
          targetResouce
        )
      );
    }

    // 次の条件で注意エリアを塗りつぶす
    // 終了日時が注意終了日よりも前
    // 終了日時が警戒終了日よりも後
    if (
      blank.to.isSameOrBefore(cautionEndDate) &&
      blank.to.isAfter(warnEndDate)
    ) {
      highlightTimeSpans.push(
        _createHighlightTimeSpan(
          blank.from.isAfter(cautionStartDate) ? blank.from : cautionStartDate,
          cautionEndDate.isAfter(blank.to) ? blank.to : cautionEndDate,
          highlightCautinClass,
          targetResouce
        )
      );
    }
  }

  return highlightTimeSpans;
};

/**
 * highlightTimeSpans作成ヘルパー
 * @param {*} startDate 開始日時(momentJS)
 * @param {*} endDate 終了日時(momentJS)
 * @param {*} cls timeSpanのCSSクラス名
 * @param {*} resourceRecord timeSpanに紐づくリソースオブジェクト
 * @param {*} padding padding default 10
 * @returns
 */
const _createHighlightTimeSpan = (
  startDate,
  endDate,
  cls,
  resourceRecord,
  padding = 10
) => {
  return {
    startDate: startDate.toDate(),
    endDate: endDate.toDate(),
    cls: cls,
    padding: padding,
    clearExisting: false,
    resourceRecord: resourceRecord,
  };
};

/**
 * 警戒、注意期間内のイベント間の空白期間を計算する
 * @param {*} events 一つのリソースに紐づくイベント配列
 * @param {*} warnStartDate 警戒開始日時
 * @param {*} cautionEndDate 注意終了日時
 * @returns 空白期間配列 [{ from: 開始日時(momentJS), to: 終了日時(momentJS) }]
 */
const _calculateEventsBlankPeriods = (
  events,
  warnStartDate,
  cautionEndDate
) => {
  // イベントを開始日でソート
  events.sort((a, b) => {
    return moment(a.startDate).isAfter(moment(b.startDate)) ? 1 : -1;
  });

  // *** 重複、重なり期間をマージする ***
  // マージされた期間の配列 [{from: 開始日時(momentJS), to: 終了日時(momentJS)}]
  const mergedPeriods = [];
  let currentmergetdPeriod = {
    from: moment(events[0].startDate),
    to: moment(events[0].endDate),
  };
  for (let i = 1; i < events.length; i++) {
    const nextPeriod = {
      from: moment(events[i].startDate),
      to: moment(events[i].endDate),
    };

    // 現在のマージ期間と次の期間が重なっている、または接している場合
    // 現在のマージ期間を大きい方の終了日時に更新
    if (nextPeriod.from.isSameOrBefore(currentmergetdPeriod.to)) {
      currentmergetdPeriod.to = moment.max(
        currentmergetdPeriod.to,
        nextPeriod.to
      );
    } else {
      // 重なっていない場合は、現在のマージ期間を追加して次の期間を開始
      mergedPeriods.push(currentmergetdPeriod);
      currentmergetdPeriod = { ...nextPeriod };
    }
  }
  // 最後のマージ期間を追加
  mergedPeriods.push(currentmergetdPeriod);

  // *** 空白期間を計算 ***
  // 空白期間配列 [{ from: 開始日時(momentJS), to: 終了日時(momentJS) }]
  const blankPeriods = [];

  // 警戒開始日時が最初の期間の開始日時より前の場合
  // 警戒開始日時から最初の期間の開始日時までの空白期間を追加
  if (moment(warnStartDate).isBefore(mergedPeriods[0].from)) {
    blankPeriods.push({
      from: moment(warnStartDate),
      to: mergedPeriods[0].from,
    });
  }

  for (let i = 0; i < mergedPeriods.length - 1; i++) {
    const currentPeriod = mergedPeriods[i];
    const nextPeriod = mergedPeriods[i + 1];
    // 現在の期間の終了日時と次の期間の開始日時が重なっていない場合
    // 空白期間を追加
    if (currentPeriod.to.isBefore(nextPeriod.from)) {
      blankPeriods.push({
        from: currentPeriod.to,
        to: nextPeriod.from,
      });
    }
  }

  // 最後の期間の終了日時が注意終了日時より前の場合
  // 最後の期間の終了日時から注意終了日時までの空白期間を追加
  if (
    moment(mergedPeriods[mergedPeriods.length - 1].to).isBefore(cautionEndDate)
  ) {
    blankPeriods.push({
      from: mergedPeriods[mergedPeriods.length - 1].to,
      to: moment(cautionEndDate),
    });
  }

  return blankPeriods;
};

export default {
  isSameEventType,
  isSameVesselEvent,
  isSameVessel,
  getRootResource,
  createUnregisterdCrewAlertTimeSpan,
};
